import { Box, Collapse, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDown from '../../../../assets/icons/arrow-down.svg';
import CheckIcon from '../../../../assets/icons/check-icon.svg';
import ClockCheckIcon from '../../../../assets/icons/clock-check-icon.svg';
import ClockIcon from '../../../../assets/icons/clock-icon.svg';
import GreenClock from '../../../../assets/icons/green-clock.svg';
import InfoIcon from '../../../../assets/icons/info-circle-icon.svg';
import SeatIcon from '../../../../assets/icons/seat-icon.svg';
import XIcon from '../../../../assets/icons/x-icon.svg';
import YellowClock from '../../../../assets/icons/yellow-clock.svg';
import { CustomButton } from '../../../../components/Button';
import { CustomChip } from '../../../../components/Chip';
import { LANG_TEXT } from '../../../../constants/language';
import { useAuthContext } from '../../../../context/authContext';
import { convertText, formatFirebaseTime } from '../../../../utils/common';
import { formatSeatInfo, getOverallStatus, multiOrderIndicator } from '../../../../utils/multiOrder.utils';
import { STATUS_COLOR_MAP } from '../../data';
import { DamagedItemStatus, MultiOrderSegment, Order, OrderItemStatus, Restaurant } from '../../order.types';

interface MultiOrderItemProps {
  item: Order;
  disabled?: boolean;
  onClickCTA?: (item: Order, restaurantIds: string[]) => void;
  onShowInfo: (item: Order) => void;
  onDamage: (item: Order, restaurantId: string) => void;
  restaurantList: Restaurant[];
  seatDataMap?: Map<string, any>;
  claimed: boolean;
}

const MultiOrderItem: React.FC<MultiOrderItemProps> = ({ item, onClickCTA, onShowInfo, disabled, onDamage, restaurantList, seatDataMap, claimed }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [expanded, setExpanded] = useState(false);
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<string[]>([]);

  const information = useMemo(() => {
    if (!item.order_table_number || !seatDataMap) return null;
    return seatDataMap.get(item.order_table_number) || null;
  }, [item.order_table_number, seatDataMap]);

  const totalStatus = useMemo(() => {
    const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid);
    return myOrders?.length ? getOverallStatus(myOrders || []) : null;
  }, [item.multi_orders, user?.uid]);

  const renderButton = useMemo(() => {
    if (disabled) {
      return (
        <Box display="flex" gap="8px" marginTop="8px">
          <img src={InfoIcon} width={14} height={14} alt="user" />
          <Typography color="#667085" lineHeight="18px" fontWeight={400} fontSize="12px">
            {t(LANG_TEXT.ORDER_DISABLE_TOOLTIP)}
          </Typography>
        </Box>
      );
    }

    if (item.multi_orders?.some((order) => !order.rider_id) && !claimed) {
      const unclaimedRestaurantIds = item.multi_orders?.filter((order) => !order.rider_id).map((order) => order.restaurant_id) || [];
      const selectedAvailableRestaurantIds = selectedRestaurantIds.filter((id) => unclaimedRestaurantIds.includes(id));
      const availableRestaurantIds = selectedRestaurantIds?.length
        ? selectedAvailableRestaurantIds
        : unclaimedRestaurantIds;

      return (
        <CustomButton
          sx={{ flex: '1 1 auto' }}
          backgroundColor="#4E5BA6"
          variant="contained"
          disabled={disabled || availableRestaurantIds.length === 0}
          onClick={(e) => {
            e.stopPropagation();
            if (!availableRestaurantIds.length) return;
            onClickCTA && onClickCTA(item, availableRestaurantIds);
          }}
        >
          {selectedAvailableRestaurantIds.length ? t(LANG_TEXT.IN_CHARGE_OF_THIS_FOOD_TRUCKS) : t(LANG_TEXT.IN_CHARGE_OF_ALL_FOOD_TRUCKS)}
        </CustomButton>
      );
    }

    switch (totalStatus as OrderItemStatus | DamagedItemStatus | null) {
      case DamagedItemStatus.CANCELLED:
      case DamagedItemStatus.DAMAGED_CANCELLED: {
        return (
          <CustomButton sx={{ flex: '1 1 auto', color: '#B42318 !important' }} variant="outlined" disabled>
            {t(LANG_TEXT.CANCELLED)}
          </CustomButton>
        );
      }
      case OrderItemStatus.READY_FOR_PICK_UP:
      case OrderItemStatus.DAMAGED_READY_FOR_PICK_UP: {
        return (
          <CustomButton variant="outlined" disabled sx={{ flex: '1 1 auto', color: '#667085 !important' }}>
            {t(LANG_TEXT.READY_FOR_PICK_UP)}
          </CustomButton>
        );
      }

      case OrderItemStatus.PREPARING:
      case OrderItemStatus.DAMAGED_PREPARING: {
        return (
          <CustomButton variant="outlined" disabled startIcon={<img src={ClockIcon} width={16} height={16} alt="user" />} sx={{ flex: '1 1 auto' }}>
            {t(LANG_TEXT.PREPARING)}
          </CustomButton>
        );
      }

      case OrderItemStatus.DELIVERING:
      case OrderItemStatus.DAMAGED_DELIVERING: {
        return (
          <CustomButton variant="outlined" disabled startIcon={<img src={ClockIcon} width={16} height={16} alt="user" />} sx={{ flex: '1 1 auto' }}>
            {t(LANG_TEXT.DELIVERING)}
          </CustomButton>
        );
      }

      case OrderItemStatus.DELIVERED: {
        return (
          <Box display="flex" gap="8px" flex={1}>
            <img src={ClockCheckIcon} width={17} height={17} alt="user" />
            <Typography color="#98A2B3" lineHeight="20px" fontWeight={400} fontSize="14px">
              {t(LANG_TEXT.ORDER_DELIVERED_AT, {
                date: formatFirebaseTime(item?.order_delivered_time?.seconds),
                interpolation: { escapeValue: false },
              })}
            </Typography>
          </Box>
        );
      }
    }
  }, [disabled, item, onClickCTA, selectedRestaurantIds, claimed, t, totalStatus]);

  const status = useMemo(() => {
    const statusMap = {
      [OrderItemStatus.PREPARING]: t(LANG_TEXT.PREPARING),
      [OrderItemStatus.DAMAGED_PREPARING]: t(LANG_TEXT.PREPARING),
      [OrderItemStatus.READY_FOR_PICK_UP]: t(LANG_TEXT.READY_FOR_PICK_UP),
      [OrderItemStatus.DAMAGED_READY_FOR_PICK_UP]: t(LANG_TEXT.READY_FOR_PICK_UP),
      [OrderItemStatus.DELIVERING]: t(LANG_TEXT.DELIVERING),
      [OrderItemStatus.DAMAGED_DELIVERING]: t(LANG_TEXT.DELIVERING),
      [OrderItemStatus.DELIVERED]: t(LANG_TEXT.DELIVERED),
      [DamagedItemStatus.CANCELLED]: t(LANG_TEXT.CANCELLED),
      [DamagedItemStatus.DAMAGED_CANCELLED]: t(LANG_TEXT.CANCELLED),
    };

    return convertText(claimed && totalStatus ? statusMap[totalStatus] : t(LANG_TEXT.NEW));
  }, [t, claimed, totalStatus]);

  const restaurantOrdersData = useMemo(() => {
    const getFoodsForRestaurant = (restaurantId: string) => {
      const restaurantFoods = item.foods?.filter((food) => food.restaurantId === restaurantId) || [];
      const promotionFoods = item.promotion_foods?.filter((food) => food.restaurantId === restaurantId) || [];
      return [...restaurantFoods, ...promotionFoods];
    };

    const getRestaurantName = (restaurantId: string) => {
      const restaurant = restaurantList.find((r) => r.restaurant_id === restaurantId);
      return restaurant?.name || restaurantId;
    };

    return (
      item.multi_orders?.map((multiOrder) => {
        const foods = getFoodsForRestaurant(multiOrder.restaurant_id);
        const restaurantName = getRestaurantName(multiOrder.restaurant_id);
        const totalQuantity = foods.reduce(
          (total, food) => total + (food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity),
          0
        );
        const isDisabled = !multiOrder.rider_id ? claimed : multiOrder.rider_id !== user?.uid;

        return {
          multiOrder,
          foods,
          restaurantName,
          totalQuantity,
          isDisabled: isDisabled || multiOrder.status === DamagedItemStatus.CANCELLED || multiOrder.status === DamagedItemStatus.DAMAGED_CANCELLED,
        };
      }) || []
    );
  }, [item.multi_orders, item.foods, item.promotion_foods, restaurantList, claimed, user?.uid]);

  const getMultiOrderButton = (multiOrder: MultiOrderSegment) => {
    if (multiOrder.rider_id) {
      const isMyOrder = multiOrder.rider_id === user?.uid;

      switch (multiOrder.status) {
        case DamagedItemStatus.CANCELLED:
        case DamagedItemStatus.DAMAGED_CANCELLED: {
          return (
            <CustomButton sx={{ fontSize: 14, height: 32, color: '#B42318 !important' }} variant="outlined" disabled>
              {t(LANG_TEXT.CANCELLED)}
            </CustomButton>
          );
        }
        case OrderItemStatus.READY_FOR_PICK_UP:
        case OrderItemStatus.DAMAGED_READY_FOR_PICK_UP: {
          return (
            <CustomButton
              sx={{ fontSize: 14, height: 32 }}
              backgroundColor="#4E5BA6"
              variant="contained"
              padding="0 6px"
              disabled={!isMyOrder || disabled}
              onClick={(e) => {
                e.stopPropagation();
                onClickCTA?.(item, [multiOrder.restaurant_id]);
              }}
            >
              {t(LANG_TEXT.PICKED_UP_FOOD)}
            </CustomButton>
          );
        }
        case OrderItemStatus.PREPARING:
        case OrderItemStatus.DAMAGED_PREPARING:
          return (
            <CustomButton sx={{ fontSize: 14, height: 32 }} variant="outlined" disabled>
              {t(LANG_TEXT.PREPARING)}
            </CustomButton>
          );
        case OrderItemStatus.DELIVERING:
        case OrderItemStatus.DAMAGED_DELIVERING: {
          return (
            <CustomButton
              sx={{ fontSize: 14, height: 32 }}
              disabled={!isMyOrder || disabled}
              variant="contained"
              padding="0 6px"
              onClick={(e) => {
                e.stopPropagation();
                onDamage(item, multiOrder.restaurant_id);
              }}
            >
              {t(LANG_TEXT.DAMAGED)}
            </CustomButton>
          );
        }
        case OrderItemStatus.DELIVERED: {
          return (
            <CustomButton sx={{ fontSize: 14, height: 32 }} variant="outlined" disabled>
              {t(LANG_TEXT.DELIVERED)}
            </CustomButton>
          );
        }
        default: {
          return (
            <CustomButton backgroundColor="#4E5BA6" variant="contained" disabled>
              <img src={CheckIcon} width={16} height={16} alt="check" />
            </CustomButton>
          );
        }
      }
    }

    const picked = selectedRestaurantIds.includes(multiOrder.restaurant_id);

    return (
      <CustomButton
        backgroundColor={picked ? '#4E5BA6' : '#EAECF0'}
        variant="contained"
        disabled={disabled || claimed}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedRestaurantIds((prev) => {
            if (prev.includes(multiOrder.restaurant_id)) return prev.filter((id) => id !== multiOrder.restaurant_id);
            return [...prev, multiOrder.restaurant_id];
          });
        }}
      >
        <img src={picked ? CheckIcon : XIcon} width={16} height={16} alt="check" />
      </CustomButton>
    );
  };

  const deliveryRequestValue = item.delivery_request || 'WHEN_ITS_READY';
  const isWhenItsReady = deliveryRequestValue === 'WHEN_ITS_READY';
  const deliveryClockIcon = isWhenItsReady ? GreenClock : YellowClock;
  const deliveryTextColor = isWhenItsReady ? 'var(--Success-600, #3D9F4A)' : 'var(--Warning-600, #FDB022)';
  const deliveryRequestDisplay = isWhenItsReady ? t(deliveryRequestValue) : deliveryRequestValue;
  const preOrderDeliveryDisplay =
    isWhenItsReady || !item.requested_delivery_time
      ? t(deliveryRequestValue)
      : deliveryRequestValue || dayjs(item.requested_delivery_time.toDate()).format('HH:mm');

  return (
    <Box
      sx={{ opacity: disabled ? 0.4 : 1, backgroundColor: 'white', borderRadius: '8px' }}
      padding="16px"
      borderBottom="1px solid #EAECF0"
      role="button"
      onClick={() => {
        if (!disabled) onShowInfo(item);
      }}
    >
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="column" gap="16px" marginBottom="16px">
          {/* Header */}
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <CustomChip size="md" bgcolor="#F2F4F7" color="#344054" height="max-content">
              {item.id} {item.has_damaged_order ? ` (${t(LANG_TEXT.DAMAGED)})` : ''}
            </CustomChip>
            <Box display="flex" flexDirection="row" gap="8px">
              <CustomChip
                size="md"
                bgcolor={STATUS_COLOR_MAP[totalStatus || OrderItemStatus.PREPARING].bgColor}
                color={STATUS_COLOR_MAP[totalStatus || OrderItemStatus.PREPARING].color}
                textTransform="capitalize"
                height="max-content"
              >
                {status}
              </CustomChip>
            </Box>
          </Box>

          {/* Name */}
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Typography color="#475467" lineHeight="28px" fontWeight={600} fontSize="18px">
              {t(LANG_TEXT.MULTI_ORDER)}
            </Typography>
            <CustomChip size="md" bgcolor="#EAECF0" color="#98A2B3" height="max-content">
              {t(LANG_TEXT.NUMBER_OF_STORES)}: <span style={{ color: '#344054' }}>{multiOrderIndicator(item)}</span>
            </CustomChip>
          </Box>

          <Box
            display="inline-flex"
            justifyContent="center"
            alignItems="center"
            gap="8px"
            bgcolor="#F2F4F7"
            color="#344054"
            borderRadius="999px"
            padding="2px 4px"
            width="fit-content"
            mx="auto"
          >
            {!item.is_pre_order && (
              <Box display="flex" alignItems="center" gap="4px">
                <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                  {deliveryRequestDisplay}
                </Typography>
              </Box>
            )}
            {item.is_pre_order && item.requested_delivery_time && (
              <Box display="flex" alignItems="center" gap="4px">
                <img src={deliveryClockIcon} height={16} width={16} alt="DeliveryClock" />
                <Typography fontSize={12} lineHeight={'20px'} color={deliveryTextColor}>
                  {preOrderDeliveryDisplay}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Food List by Restaurant */}
          <Box display="flex" flexDirection="column" gap="16px">
            {restaurantOrdersData.map(({ foods, restaurantName }, index) => {
              if (foods.length === 0) return null;

              return (
                <Box key={index} display="flex" flexDirection="column" gap="4px">
                  <Typography color="#475467" lineHeight="28px" fontWeight={600} fontSize="18px">
                    {restaurantName}
                  </Typography>
                  {foods.map((food, foodIndex) => {
                    const quantity = food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity;

                    if (quantity <= 0) return null;

                    return (
                      <Typography key={foodIndex} color="#475467" lineHeight="18px" fontWeight={400} fontSize="14px">
                        {food.item_name}: {quantity} {t(LANG_TEXT.ORDER_ITEMS)}
                      </Typography>
                    );
                  })}
                </Box>
              );
            })}
          </Box>

          {/* Name */}
          <Box display="flex" alignItems="center" justifyContent="start">
            <Box display="flex" gap="8px" alignItems="center">
              <img src={SeatIcon} width={24} height={24} alt="user" />
              <Typography color="#475467" lineHeight="24px" fontWeight={500} fontSize="16px">
                {information ? formatSeatInfo(information) : '-'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Collapse in={expanded} sx={{ marginBottom: expanded ? '24px' : 0 }}>
          <Box display="flex" flexDirection="column" gap="24px">
            {restaurantOrdersData.map(({ multiOrder, restaurantName, totalQuantity, isDisabled }, index) => (
              <Box
                key={index}
                padding="16px"
                display="flex"
                gap="16px"
                sx={{ borderBottom: '1px solid var(--Gray-200, #EAECF0);', backgroundColor: isDisabled ? '#F2F4F7' : 'transparent' }}
              >
                <Box sx={{ flex: '1' }}>
                  <Typography fontWeight={500} fontSize="12px" color="#1D2939" marginBottom="4px">
                    {restaurantName}
                  </Typography>
                  <Typography fontWeight={500} fontSize="12px" color="#1D2939">
                    {t(LANG_TEXT.QUANTITY)}: {totalQuantity} {t(LANG_TEXT.ORDER_ITEMS)}
                  </Typography>
                </Box>
                {getMultiOrderButton(multiOrder)}
              </Box>
            ))}
          </Box>
        </Collapse>

        <Box display="flex" gap="20px" width="100%" alignItems="center">
          {renderButton}
          <CustomButton
            backgroundColor="#EAECF0"
            variant="contained"
            sx={{ minWidth: '52px', height: '52px' }}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <img
              src={ArrowDown}
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
              width={16}
              height={16}
              alt="arrow down"
            />
          </CustomButton>
        </Box>
      </Box>
    </Box>
  );
};

export default MultiOrderItem;
